import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { AppLayout } from "../shared/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Eye,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  MailOpen,
  ExternalLink,
  AlertTriangle,
  FileWarning,
  MailX,
  Inbox,
  Play,
  Trash2,
  Globe,
  FileText,
  List,
  History,
  PenTool,
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { countryOptions } from "@/utils/country-options";
import { getCommissionsForReminderTool, sendBulkReminderMails } from "@/data/requests/invoice-requests";
import {
  getMailSendOperations,
  retryEmail,
  bulkRetryEmails,
  getEmailQueueStatus,
  processEmailQueue,
  clearCompletedQueueItems,
  getEmailTransactionById,
  getEmailStatistics,
} from "../../../data";

const STATUS_CONFIG = {
  PENDING: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  QUEUED: { label: "Queued", color: "bg-blue-100 text-blue-800", icon: Clock },
  SENT: { label: "Sent", color: "bg-green-100 text-green-800", icon: Send },
  DELIVERED: { label: "Delivered", color: "bg-green-100 text-green-800", icon: CheckCircle },
  OPENED: { label: "Opened", color: "bg-purple-100 text-purple-800", icon: MailOpen },
  FAILED: { label: "Failed", color: "bg-red-100 text-red-800", icon: XCircle },
  BOUNCED: { label: "Bounced", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

const EMAIL_TYPE_CONFIG = {
  INVOICE_REQUEST: { label: "Invoice Request", color: "bg-blue-100 text-blue-800" },
  REMINDER: { label: "Reminder", color: "bg-orange-100 text-orange-800" },
  CHANGE_REQUEST: { label: "Change Request", color: "bg-yellow-100 text-yellow-800" },
  CONFIRMATION: { label: "Confirmation", color: "bg-green-100 text-green-800" },
  CUSTOM: { label: "Custom", color: "bg-muted text-muted-foreground" },
};

const FILTER_OPTIONS = [
  { value: "all", label: "All Operations", icon: Inbox },
  { value: "pending_invoice", label: "Pending Invoice (1+ month)", icon: FileWarning },
  { value: "pending_change_request", label: "Pending Change Request", icon: AlertTriangle },
  { value: "failed", label: "Failed Emails", icon: MailX },
  { value: "not_opened", label: "Not Opened", icon: Mail },
];

const COUNTRY_OPTIONS = countryOptions
  .map((option) => {
    const normalizedValue = typeof option?.value === "string" ? option.value.trim() : option?.value;
    return {
      ...option,
      value: normalizedValue,
    };
  })
  .filter((option) => option?.value);

export function MailOperationsScreen() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("transactions");
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [commissionDetails, setCommissionDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSendingMail, setIsSendingMail] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedMailType, setSelectedMailType] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["mailOperations", page, filter],
    queryFn: () => getMailSendOperations({ page, limit: 20, filter }),
  });

  const { data: queueStatus } = useQuery({
    queryKey: ["emailQueueStatus"],
    queryFn: getEmailQueueStatus,
    refetchInterval: 10000,
  });

  const { data: statistics } = useQuery({
    queryKey: ["emailStatistics"],
    queryFn: () => getEmailStatistics({}),
  });

  const retryMutation = useMutation({
    mutationFn: (id) => retryEmail(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["mailOperations"]);
      queryClient.invalidateQueries(["emailQueueStatus"]);
    },
  });

  const bulkRetryMutation = useMutation({
    mutationFn: (ids) => bulkRetryEmails(ids),
    onSuccess: () => {
      setSelectedIds([]);
      queryClient.invalidateQueries(["mailOperations"]);
      queryClient.invalidateQueries(["emailQueueStatus"]);
    },
  });

  const processQueueMutation = useMutation({
    mutationFn: processEmailQueue,
    onSuccess: () => queryClient.invalidateQueries(["emailQueueStatus"]),
  });

  const clearCompletedMutation = useMutation({
    mutationFn: () => clearCompletedQueueItems(0),
    onSuccess: () => queryClient.invalidateQueries(["emailQueueStatus"]),
  });

  const loadCommissionDetails = async (country, mailType) => {
    if (!country || !mailType) return;

    setIsLoadingDetails(true);
    try {
      const response = await getCommissionsForReminderTool(country, mailType);
      setCommissionDetails(response.data);
    } catch (error) {
      console.error("Error loading commission details:", error);
      toast({
        title: "Detaylar yüklenemedi",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setValue("country", value);
    if (selectedMailType) {
      loadCommissionDetails(value, selectedMailType);
    }
  };

  const handleMailTypeChange = (value) => {
    setSelectedMailType(value);
    setValue("mailType", value);
    if (selectedCountry) {
      loadCommissionDetails(selectedCountry, value);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEmailTypeLabel = (type) => {
    const labels = {
      INVOICE_REQUEST: "Fatura Talebi",
      REMINDER: "Hatırlatma",
      CHANGE_REQUEST: "Değişiklik Talebi",
      CONFIRMATION: "Onay",
      CUSTOM: "Özel",
    };
    return labels[type] || type;
  };

  const onSubmitReminderMail = async () => {
    if (!commissionDetails || commissionDetails.totalCommissions === 0) {
      toast({ title: "Komisyon Yok", description: "Mail gönderilecek komisyon bulunamadı", variant: "destructive" });
      return;
    }

    if (!selectedCountry || !selectedMailType) {
      toast({ title: "Eksik Bilgi", description: "Lütfen ülke ve mail tipi seçin", variant: "destructive" });
      return;
    }

    if (selectedRecipients.length === 0) {
      toast({ title: "Seçim Yapılmadı", description: "Lütfen mail gönderilecek kişileri seçin", variant: "destructive" });
      return;
    }

    setIsSendingMail(true);
    try {
      const response = await sendBulkReminderMails(selectedCountry, {
        mailType: selectedMailType,
        mailBodyMessage: getValues("mailBodyMessage"),
        useQueue: true,
        selectedRecipients: selectedRecipients,
      });

      toast({
        title: "Mail Gönderimi Başlatıldı",
        description: `${response.data.count} kişiye mail kuyruğa eklendi`,
      });
      setCommissionDetails(null);
      setSelectedCountry("");
      setSelectedMailType("");
      setSelectedRecipients([]);
      queryClient.invalidateQueries(["emailQueueStatus"]);
    } catch (error) {
      console.error("Error sending bulk mails:", error);
      toast({
        title: "Mail Gönderimi Başarısız",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    } finally {
      setIsSendingMail(false);
    }
  };

  const handlePreview = async (transaction) => {
    try {
      const response = await getEmailTransactionById(transaction.id);
      setSelectedTransaction(response.data);
      setPreviewOpen(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(transactions.filter((t) => t.status === "FAILED").map((t) => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  const handleRecipientSelectAll = (checked) => {
    if (checked && commissionDetails?.commissions) {
      setSelectedRecipients(commissionDetails.commissions.map(c => c.id));
    } else {
      setSelectedRecipients([]);
    }
  };

  const handleRecipientSelectOne = (id, checked) => {
    setSelectedRecipients((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  const transactions = data?.data?.data || [];
  const summary = data?.data?.summary || {};
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: 0 };
  const queue = queueStatus?.data || {};
  const stats = statistics?.data || {};
  const selectFilterValue = filter || "all";

  return (
    <AppLayout title="Mail Send Operations">
      <div className="px-4 py-6 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="transactions" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Email Transactions
            </TabsTrigger>
            <TabsTrigger value="reminder" className="flex items-center gap-2">
              <PenTool className="h-4 w-4" />
              Hatırlatma Maili Gönder
            </TabsTrigger>
          </TabsList>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Sent</p>
                      <p className="text-2xl font-bold">{stats.summary?.totalSent || 0}</p>
                    </div>
                    <Send className="h-8 w-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Open Rate</p>
                      <p className="text-2xl font-bold">{stats.summary?.openRate || 0}%</p>
                    </div>
                    <MailOpen className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Failed</p>
                      <p className="text-2xl font-bold text-red-600">{stats.summary?.totalFailed || 0}</p>
                    </div>
                    <XCircle className="h-8 w-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Queue Pending</p>
                      <p className="text-2xl font-bold text-blue-600">{queue.pending || 0}</p>
                    </div>
                    <Clock className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Queue Status */}
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Inbox className="h-5 w-5 text-blue-600" />
                    Email Queue Status
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => processQueueMutation.mutate()}
                      disabled={processQueueMutation.isPending}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Process Queue
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => clearCompletedMutation.mutate()}
                      disabled={clearCompletedMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Clear Completed
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-5 gap-4 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-xl font-bold text-yellow-600">{queue.pending || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Processing</p>
                    <p className="text-xl font-bold text-blue-600">{queue.processing || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-xl font-bold text-green-600">{queue.completed || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-xl font-bold text-red-600">{queue.failed || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className={queue.isProcessing ? "bg-green-100 text-green-800" : "bg-muted text-foreground"}>
                      {queue.isProcessing ? "Running" : "Idle"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card
                className={`cursor-pointer transition-all ${filter === "pending_invoice" ? "ring-2 ring-orange-500" : ""}`}
                onClick={() => setFilter("pending_invoice")}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Invoice (1+ month)</p>
                      <p className="text-2xl font-bold text-orange-600">{summary.pendingInvoice || 0}</p>
                    </div>
                    <FileWarning className="h-8 w-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-all ${filter === "pending_change_request" ? "ring-2 ring-yellow-500" : ""}`}
                onClick={() => setFilter("pending_change_request")}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Change Request</p>
                      <p className="text-2xl font-bold text-yellow-600">{summary.pendingChangeRequest || 0}</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-all ${filter === "failed" ? "ring-2 ring-red-500" : ""}`}
                onClick={() => setFilter("failed")}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Failed Emails</p>
                      <p className="text-2xl font-bold text-red-600">{summary.failed || 0}</p>
                    </div>
                    <MailX className="h-8 w-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-all ${filter === "not_opened" ? "ring-2 ring-gray-500" : ""}`}
                onClick={() => setFilter("not_opened")}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Not Opened</p>
                      <p className="text-2xl font-bold text-muted-foreground">{summary.notOpened || 0}</p>
                    </div>
                    <Mail className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Transactions table */}
            <Card>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="h-5 w-5" />
                    Email Operations ({pagination.total})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select
                      value={selectFilterValue}
                      onValueChange={(value) => setFilter(value === "all" ? "" : value)}
                    >
                      <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder="Select Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        {FILTER_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <option.icon className="h-4 w-4" />
                              {option.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedIds.length > 0 && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => bulkRetryMutation.mutate(selectedIds)}
                        disabled={bulkRetryMutation.isPending}
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${bulkRetryMutation.isPending ? "animate-spin" : ""}`} />
                        Retry Selected ({selectedIds.length})
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="py-12 text-center">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                    <p className="mt-2 text-muted-foreground">Loading...</p>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-400" />
                    <p className="text-lg font-medium">All caught up!</p>
                    <p className="text-sm">No pending operations found</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={
                                selectedIds.length > 0 &&
                                selectedIds.length === transactions.filter((t) => t.status === "FAILED").length
                              }
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          <TableHead>Recipient</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Opens</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((transaction) => {
                          const statusConfig = STATUS_CONFIG[transaction.status] || STATUS_CONFIG.PENDING;
                          const typeConfig = EMAIL_TYPE_CONFIG[transaction.emailType] || EMAIL_TYPE_CONFIG.CUSTOM;
                          const StatusIcon = statusConfig.icon;

                          return (
                            <TableRow key={transaction.id}>
                              <TableCell>
                                {transaction.status === "FAILED" && (
                                  <Checkbox
                                    checked={selectedIds.includes(transaction.id)}
                                    onCheckedChange={(checked) => handleSelectOne(transaction.id, checked)}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{transaction.recipientName || "-"}</p>
                                  <p className="text-sm text-muted-foreground">{transaction.recipientEmail}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${statusConfig.color} flex items-center gap-1 w-fit`}>
                                  <StatusIcon className="h-3 w-3" />
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {transaction.client ? (
                                  <Button
                                    variant="link"
                                    className="p-0 h-auto"
                                    onClick={() => navigate(`/clients/${transaction.client.id}`)}
                                  >
                                    {transaction.client.name}
                                  </Button>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                {format(new Date(transaction.createdAt), "dd/MM/yyyy HH:mm")}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                  <span>{transaction.openCount || 0}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button variant="ghost" size="sm" onClick={() => handlePreview(transaction)}>
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                  {transaction.status === "FAILED" && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => retryMutation.mutate(transaction.id)}
                                      disabled={retryMutation.isPending}
                                    >
                                      <RefreshCw
                                        className={`h-4 w-4 ${retryMutation.isPending ? "animate-spin" : ""}`}
                                      />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t">
                        <div className="text-sm text-muted-foreground">
                          Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            disabled={page === 1}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((prev) => Math.min(pagination.totalPages, prev + 1))}
                            disabled={page === pagination.totalPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reminder Tab */}
          <TabsContent value="reminder" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Mail className="h-6 w-6 text-blue-600" />
                  <div>
                    <CardTitle className="text-2xl">Hatırlatma Mail Aracı</CardTitle>
                    <CardDescription>
                      Müşterilere ülke ve fatura seçimine göre hatırlatma maili gönderin
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            <form onSubmit={handleSubmit(onSubmitReminderMail)} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      Ayarlar
                    </CardTitle>
                    <CardDescription>Mail gönderim parametrelerini ayarlayın</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Ülke Seçin
                      </Label>
                      <Select onValueChange={handleCountryChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Ülke seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Countries</SelectLabel>
                            {COUNTRY_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Mail Tipi</Label>
                      <Select onValueChange={handleMailTypeChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Mail tipi seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reminder">Hatırlatma Maili (Fatura Yüklemeyenler)</SelectItem>
                          <SelectItem value="change_request">Düzeltme Talebi (Tekrar Yüklemeyenler)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Mail İçeriği
                    </CardTitle>
                    <CardDescription>Mail konusu ve mesajınızı yazın</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Mail Konusu</Label>
                      <Input
                        placeholder="Mail konusunu girin..."
                        {...register("subject", { required: "Konu zorunludur" })}
                      />
                      {errors.subject && (
                        <p className="text-sm text-red-500">{errors.subject.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Mail Mesajı</Label>
                      <div className="border rounded-lg overflow-hidden">
                        <ReactQuill
                          theme="snow"
                          placeholder="Mail mesajınızı buraya yazın..."
                          onChange={(value) => setValue("mailBodyMessage", value)}
                          style={{ minHeight: "200px", backgroundColor: "white" }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Commission Details Table */}
              {isLoadingDetails ? (
                <Card>
                  <CardContent className="py-12">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-8 w-8 animate-spin text-primary mr-2" />
                      <span>Komisyon detayları yükleniyor...</span>
                    </div>
                  </CardContent>
                </Card>
              ) : commissionDetails && commissionDetails.commissions.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5" />
                      Mail Gönderilecek Kişiler ({commissionDetails.totalCommissions})
                    </CardTitle>
                    <CardDescription>
                      {selectedMailType === "reminder" 
                        ? "\"Mail Sended\" durumundaki komisyonlar (fatura yüklemeyenler)"
                        : "\"Change Request\" durumundaki komisyonlar (tekrar yüklemeyenler)"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[400px] overflow-auto rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={
                                  selectedRecipients.length > 0 &&
                                  selectedRecipients.length === commissionDetails?.commissions?.length
                                }
                                onCheckedChange={handleRecipientSelectAll}
                              />
                            </TableHead>
                            <TableHead>İsim</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Tutar</TableHead>
                            <TableHead>Son Mail Tarihi</TableHead>
                            <TableHead>Son Mail Tipi</TableHead>
                            <TableHead>Açılma</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {commissionDetails.commissions.map((commission) => (
                            <TableRow key={commission.id}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedRecipients.includes(commission.id)}
                                  onCheckedChange={(checked) => handleRecipientSelectOne(commission.id, checked)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {commission.influencerName}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {commission.email}
                              </TableCell>
                              <TableCell>
                                ${commission.finalAmount?.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                {commission.lastEmail ? (
                                  <div className="flex items-center gap-1 text-sm">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(commission.lastEmail.sentAt || commission.lastEmail.createdAt)}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {commission.lastEmail ? (
                                  <Badge variant="outline" className="text-xs">
                                    {getEmailTypeLabel(commission.lastEmail.type)}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {commission.lastEmail && commission.lastEmail.openCount > 0 ? (
                                  <div className="flex items-center gap-1 text-green-600">
                                    <Eye className="h-3 w-3" />
                                    {commission.lastEmail.openCount}x
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ) : commissionDetails && commissionDetails.commissions.length === 0 ? (
                <Card>
                  <CardContent className="py-12">
                    <div className="text-center text-muted-foreground">
                      <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Seçilen kriterlere uygun komisyon bulunamadı.</p>
                      <p className="text-sm">
                        {selectedMailType === "reminder" 
                          ? "\"Mail Sended\" durumunda komisyon yok."
                          : "\"Change Request\" durumunda komisyon yok."}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {commissionDetails ? (
                        <span><strong>{selectedRecipients.length}</strong> kişi seçildi (toplam {commissionDetails.totalCommissions})</span>
                      ) : (
                        <span>Ülke ve mail tipi seçin</span>
                      )}
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSendingMail || !commissionDetails || selectedRecipients.length === 0}
                      className="min-w-[150px]"
                    >
                      {isSendingMail ? (
                        "Gönderiliyor..."
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Mail Gönder ({selectedRecipients.length})
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </TabsContent>
        </Tabs>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Transaction Details
              </DialogTitle>
            </DialogHeader>
            {selectedTransaction && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Recipient</Label>
                    <p className="text-sm">{selectedTransaction.recipientEmail}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                    <Badge className={STATUS_CONFIG[selectedTransaction.status]?.color}>
                      {STATUS_CONFIG[selectedTransaction.status]?.label}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Type</Label>
                    <Badge className={EMAIL_TYPE_CONFIG[selectedTransaction.emailType]?.color}>
                      {EMAIL_TYPE_CONFIG[selectedTransaction.emailType]?.label}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Tracking Code</Label>
                    <code className="text-xs bg-muted px-2 py-1 rounded block mt-1">
                      {selectedTransaction.trackingCode}
                    </code>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-muted-foreground">Subject</Label>
                  <p className="text-sm mt-1">{selectedTransaction.subject}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 border-t pt-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Created</Label>
                    <p className="text-sm">
                      {format(new Date(selectedTransaction.createdAt), "dd/MM/yyyy HH:mm")}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Sent</Label>
                    <p className="text-sm">
                      {selectedTransaction.sentAt
                        ? format(new Date(selectedTransaction.sentAt), "dd/MM/yyyy HH:mm")
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Opened</Label>
                    <p className="text-sm">
                      {selectedTransaction.openedAt
                        ? format(new Date(selectedTransaction.openedAt), "dd/MM/yyyy HH:mm")
                        : "-"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Open Count</Label>
                    <p className="text-sm flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {selectedTransaction.openCount || 0} times
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Retry Count</Label>
                    <p className="text-sm">
                      {selectedTransaction.retryCount || 0} / {selectedTransaction.maxRetries || 3}
                    </p>
                  </div>
                </div>

                {selectedTransaction.failureReason && (
                  <div className="border-t pt-4">
                    <Label className="text-sm font-medium text-red-500">Failure Reason</Label>
                    <p className="text-sm text-red-600 mt-1 bg-red-50 dark:bg-red-900 p-2 rounded">
                      {selectedTransaction.failureReason}
                    </p>
                  </div>
                )}

                {selectedTransaction.metadata && (
                  <div className="border-t pt-4">
                    <Label className="text-sm font-medium text-muted-foreground">Metadata</Label>
                    <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                      {JSON.stringify(selectedTransaction.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

export default MailOperationsScreen;
